import { useCallback, useEffect, useState } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import {
	fetchMergeCandidates,
	resolveMergeCandidate,
	type MergeCandidate,
	type MergeCandidateProduct
} from '@/lib/api';

/**
 * Pairs dedup could not decide, queued for a human.
 *
 * Dedup deliberately refuses to merge on weak evidence — a false merge shows
 * one product's price on another's page. That leaves genuine duplicates with
 * imperfect data sitting apart, and this is where they surface.
 *
 * Merging is irreversible in one direction: the losing product's row is deleted
 * (its source rows survive and move across). So the copy leans towards "reject
 * unless clearly the same", and the evidence for each pair is shown in full
 * rather than summarised.
 */

const REASON_LABEL: Record<MergeCandidate['reason'], string> = {
	mpn_match_upc_conflict: 'Same part number, conflicting barcodes',
	mpn_match_upc_missing: 'Same part number, no barcode to confirm',
	title_near_match: 'Near-identical titles, no identifiers'
};

const REASON_HELP: Record<MergeCandidate['reason'], string> = {
	mpn_match_upc_conflict:
		'Often a variant sharing a part number — but sometimes one retailer’s barcode is simply wrong. Check the titles for a size, length or voltage difference.',
	mpn_match_upc_missing:
		'The part numbers agree and there is no barcode on one side to confirm or deny it.',
	title_near_match:
		'Nothing but the titles to go on. Treat with the most suspicion of the three.'
};

function ProductColumn({ p, label }: { p: MergeCandidateProduct; label: string }) {
	return (
		<div className="flex-1 min-w-0">
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography className="font-medium break-words">{p.title}</Typography>
			<dl className="mt-2 space-y-0.5 text-xs text-gray-600">
				<div>
					<dt className="inline font-medium">Brand: </dt>
					<dd className="inline">{p.brand ?? '—'}</dd>
				</div>
				<div>
					<dt className="inline font-medium">Part no: </dt>
					<dd className="inline">{p.mpn ?? '—'}</dd>
				</div>
				<div>
					<dt className="inline font-medium">Barcode: </dt>
					<dd className="inline">{p.upc ?? '—'}</dd>
				</div>
			</dl>
		</div>
	);
}

function MergeReviewView() {
	const [candidates, setCandidates] = useState<MergeCandidate[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<string | null>(null);

	const load = useCallback(async () => {
		try {
			setCandidates(await fetchMergeCandidates('pending'));
			setError(null);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	async function resolve(id: string, action: 'merge' | 'reject') {
		setBusy(id);
		try {
			await resolveMergeCandidate(id, action);
			setCandidates((c) => (c ?? []).filter((x) => x.id !== id));
			setError(null);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(null);
		}
	}

	return (
		<FusePageSimple
			header={
				<div className="p-6">
					<Typography variant="h5" className="font-semibold">
						Merge review
					</Typography>
					<Typography color="text.secondary" variant="body2">
						Products that may be the same item. Dedup would not decide these on its own.
					</Typography>
				</div>
			}
			content={
				<div className="p-6 flex flex-col gap-4 max-w-4xl">
					{error && <Alert severity="error">{error}</Alert>}

					{candidates === null && <CircularProgress size={24} />}

					{candidates?.length === 0 && (
						<Alert severity="success">
							Nothing to review. Every pair dedup saw was either a confident match or
							a confident non-match.
						</Alert>
					)}

					{candidates?.map((c) => (
						<Paper key={c.id} className="p-5">
							<div className="flex items-center gap-2 mb-1 flex-wrap">
								<Chip size="small" label={REASON_LABEL[c.reason]} color="warning" />
								{c.similarity !== null && (
									<Chip
										size="small"
										variant="outlined"
										label={`title similarity ${(c.similarity * 100).toFixed(0)}%`}
									/>
								)}
							</div>
							<Typography variant="body2" color="text.secondary" className="mb-4">
								{REASON_HELP[c.reason]}
							</Typography>

							<div className="flex flex-col sm:flex-row gap-6">
								<ProductColumn p={c.a} label="Keep this one" />
								<ProductColumn p={c.b} label="Fold in / discard" />
							</div>

							<div className="mt-5 flex gap-2">
								<Button
									variant="contained"
									color="warning"
									disabled={busy === c.id}
									onClick={() => resolve(c.id, 'merge')}
								>
									Merge — same product
								</Button>
								<Button
									variant="outlined"
									disabled={busy === c.id}
									onClick={() => resolve(c.id, 'reject')}
								>
									Keep separate
								</Button>
							</div>
							<Typography variant="caption" color="text.secondary" className="mt-2 block">
								Merging deletes the second product and moves its retailer links onto the
								first. Reject if you are not sure — the pair will not be raised again.
							</Typography>
						</Paper>
					))}
				</div>
			}
		/>
	);
}

export default MergeReviewView;
